/**
 * Plugin Document Viewer - Affichage de documents PDF uniquement
 */

import React, { useState, useEffect, useRef } from 'react';
import { pdfjs } from 'react-pdf';
import { PluginInterface, PluginPreviewProps } from '../types/PluginInterface';
import { generateUniformPlaceholder } from '../utils/PlaceholderGenerator';
import { PDFDocument } from 'pdf-lib';
import SignaturePad from 'react-signature-pad-wrapper';
import AutoTranslateText from '../components/AutoTranslateText';
import { notificationService } from '../services/notificationService';

import { DocumentViewerConfig } from './document-viewer/types';
import { DOCUMENT_TYPES } from './document-viewer/constants';
import PDFViewer from './document-viewer/PDFViewer';
import DocumentViewerConfigComponent from './document-viewer/DocumentViewerConfigComponent';
import { DocumentService } from '../services/DocumentService';

// Configuration du worker PDF.js
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const DocumentViewerRenderer: React.FC<PluginPreviewProps> = ({ config }) => {
  const settings = config as DocumentViewerConfig;
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [isSigningAreaVisible, setIsSigningAreaVisible] = useState(false);
  const [isSigning, setIsSigning] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [emailStatus, setEmailStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [signedBlobUrl, setSignedBlobUrl] = useState<string | null>(null);
  const signaturePadRef = React.useRef<SignaturePad>(null);

  // Libère le blob URL signé quand le composant est démonté ou qu'un nouveau blob est généré
  useEffect(() => {
    return () => {
      if (signedBlobUrl) URL.revokeObjectURL(signedBlobUrl);
    };
  }, [signedBlobUrl]);
  
  // ✅ Ref pour mesurer la largeur du conteneur PDF
  const pdfContainerRef = useRef<HTMLDivElement>(null);
  const [pdfWidth, setPdfWidth] = useState<number | undefined>(undefined);

  // ✅ Suppression de 'documentType' destructuré car on force PDF
  const { title, documentUrl, width, height, showDownloadButton, showFullscreenButton, fileName, enableSignature, displayMode } = settings;

  // ✅ Force type PDF uniquement
  const documentTypeInfo = DOCUMENT_TYPES.pdf;

  const bgStyle = settings.backgroundColor === 'transparent' ? 'transparent' : (settings.backgroundColor || '#ffffff');

  // ✅ OBSERVATEUR DE LARGEUR (Fit Width)
  // Permet d'adapter la largeur du PDF à celle du conteneur défini (ex: 60%)
  useEffect(() => {
    const updateWidth = () => {
      if (pdfContainerRef.current) {
        // Obtenir la largeur totale disponible (ex: 100% de l'écran)
        setPdfWidth(pdfContainerRef.current.clientWidth);
      }
    };

    // Mesure initiale
    updateWidth();

    // Détecte les changements de taille (redimensionnement fenêtre ou changement config largeur)
    const observer = new ResizeObserver(() => {
        updateWidth();
    });

    if (pdfContainerRef.current) {
        observer.observe(pdfContainerRef.current);
    }
    
    // Fallback pour redimensionnement fenêtre
    window.addEventListener('resize', updateWidth);

    return () => {
        if (pdfContainerRef.current) observer.unobserve(pdfContainerRef.current);
        observer.disconnect();
        window.removeEventListener('resize', updateWidth);
    };
  }, [settings.containerWidth, isFullscreen]);

  const handleLoadSuccess = () => setIsLoading(false);
  const handleLoadError = (error: string) => { 
    setIsLoading(false); 
    setHasError(true); 
    console.error(error); 
  };
  
  const handleDownload = () => {
    if (!documentUrl) return;
    const link = document.createElement('a');
    link.href = documentUrl;
    link.download = fileName || 'document.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const toggleFullscreen = () => {
    const elem = document.querySelector('.document-viewer-container');
    if (!elem) return;
    if (!document.fullscreenElement) {
      elem.requestFullscreen().catch(err => console.error(`Erreur mode plein écran: ${err.message}`));
    } else {
      document.exitFullscreen();
    }
  };

  const handleApplySignature = async () => {
    const signaturePad = signaturePadRef.current;
    const { signaturePositions } = config as DocumentViewerConfig;
    if (!signaturePad || signaturePad.isEmpty() || !documentUrl || !signaturePositions || signaturePositions.length === 0) return;

    setIsSigning(true);
    setIsSendingEmail(false);
    setEmailStatus('idle');
    try {
        let ownerEmail: string | null = null;
        let ownerId: number | null = null;
        try {
            const userString = localStorage.getItem('user');
            if (userString) {
            const user = JSON.parse(userString);
            ownerEmail = user?.email;
            }
        } catch (e) {
            console.error(e);
        }
        const cfg = config as DocumentViewerConfig;
        const cfgOwnerIdRaw = (cfg as any)?.userId || (cfg as any)?.ownerId;
        if (cfgOwnerIdRaw) {
          const parsed = Number(cfgOwnerIdRaw);
          if (Number.isFinite(parsed)) ownerId = parsed;
        }
        if (cfg.ownerEmail) {
          ownerEmail = cfg.ownerEmail;
        }

        const existingPdfBytes = await fetch(documentUrl).then(res => res.arrayBuffer());
        const pdfDoc = await PDFDocument.load(existingPdfBytes);
        const signatureImage = signaturePad.toDataURL('image/png');
        const pngImage = await pdfDoc.embedPng(signatureImage);
        const pngDims = pngImage.scale(0.25);

        for (const position of signaturePositions) {
            const pageIndex = position.page - 1;
            const targetPage = pdfDoc.getPages()[pageIndex];

            if (targetPage) {
            const pageHeight = targetPage.getHeight();
            const xPos = position.x - (pngDims.width / 2);
            const yPos = pageHeight - position.y - (pngDims.height / 2);

            targetPage.drawImage(pngImage, { x: xPos, y: yPos, width: pngDims.width, height: pngDims.height });
            }
        }
        
        const pdfBytes = await pdfDoc.save();
        const blob = new Blob([new Uint8Array(pdfBytes)], { type: 'application/pdf' });
        const signedFileName = `${(fileName || 'document').replace(/\.pdf$/, '')}-signed.pdf`;

        // Affiche le document signé dans le viewer
        if (signedBlobUrl) URL.revokeObjectURL(signedBlobUrl);
        const newBlobUrl = URL.createObjectURL(blob);
        setSignedBlobUrl(newBlobUrl);
        setIsLoading(true); // recharge le viewer sur le nouveau document

        // Téléchargement — blob URL séparé pour ne pas révoquer celui du viewer
        const dlUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = dlUrl;
        link.download = signedFileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(dlUrl), 10000);

        if (ownerEmail && ownerId) {
            setIsSendingEmail(true);
            try {
            await DocumentService.sendSignedDocument(blob, ownerEmail, "Document signé", fileName || 'document.pdf');
            setEmailStatus('success');
            if (Number.isFinite(ownerId)) {
              await notificationService.sendNotification(
                ownerId,
                'document_signed',
                `Document signé : ${fileName || 'document.pdf'}`,
                fileName || undefined
              );
            }
            } catch (emailError) {
            console.error(emailError);
            setEmailStatus('error');
            } finally {
            setIsSendingEmail(false);
            }
        }

        setIsSigningAreaVisible(false);
    } catch (err) { 
        console.error("Erreur signature:", err); 
    } finally { 
        setIsSigning(false); 
    }
  };

  useEffect(() => {
    const onFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  const containerStyle = React.useMemo(() => {
    if (isFullscreen) {
      return { width: '100%', height: '100%', zIndex: 1050 };
    }
    if (displayMode === 'fixed') {
      return { width: '100%', height: '100%', maxWidth: `${width}px`, maxHeight: `${height}px` };
    }
    // ✅ MODIFICATION : Le conteneur principal reste large (100%) pour occuper l'espace disponible
    return { 
      width: '100%', 
      margin: '0',
      height: 'auto', 
      minHeight: '400px', 
      maxHeight: '80vh' 
    };
  }, [isFullscreen, displayMode, width, height]);

  // Styles titre personnalisés
  const titleStyle: React.CSSProperties = {
    color: settings.titleColor || 'inherit',
    fontFamily: settings.titleFontFamily || 'inherit',
    fontSize: settings.titleFontSize || '1rem',
    fontWeight: settings.titleBold ? 'bold' : 'normal',
    fontStyle: settings.titleItalic ? 'italic' : 'normal',
    textDecoration: settings.titleUnderline ? 'underline' : 'none',
  };

  return (
    <div 
      className={`document-viewer-container ${isFullscreen ? 'position-fixed top-0 start-0 w-100 h-100 bg-dark' : ''}`} 
      style={{ ...containerStyle, display: 'flex', flexDirection: 'column', margin: '0' }}
    >
      <div 
         className="document-header px-2 py-1 border-bottom d-flex justify-content-between align-items-center flex-shrink-0"
         style={{ backgroundColor: bgStyle }}
      >
        <h6 className="mb-0 text-truncate d-none d-md-block small" style={titleStyle}>
           {documentTypeInfo.icon} {title}
        </h6>
        <div className="document-controls">
          {enableSignature && (
            <button className={`btn btn-sm me-1 ${isSigningAreaVisible ? 'btn-success' : 'btn-outline-success'}`} onClick={() => setIsSigningAreaVisible(prev => !prev)} title="Signer">
              <i className="fas fa-signature"></i> <AutoTranslateText sourceLang="fr">Signer</AutoTranslateText>
            </button>
          )}
          {showDownloadButton && (
            <button className="btn btn-sm btn-outline-primary me-1" onClick={handleDownload} title="Télécharger">
              <i className="fas fa-download"></i> <AutoTranslateText sourceLang="fr">Télécharger</AutoTranslateText>
            </button>
          )}
          {showFullscreenButton && (
            <button className="btn btn-sm btn-outline-secondary" onClick={toggleFullscreen} title="Plein écran">
              <i className={`fas ${isFullscreen ? 'fa-compress-arrows-alt' : 'fa-expand-arrows-alt'}`}></i>
            </button>
          )}
        </div>
      </div>

      {emailStatus === 'success' && (
        <div className="alert alert-success py-2 small m-2 mb-0">
          <AutoTranslateText sourceLang="fr">✅ Email envoyé avec le document signé.</AutoTranslateText>
        </div>
      )}
      {emailStatus === 'error' && (
        <div className="alert alert-warning py-2 small m-2 mb-0">
          <AutoTranslateText sourceLang="fr">⚠️ Échec d'envoi de l'email, mais le document a été téléchargé.</AutoTranslateText>
        </div>
      )}
      {signedBlobUrl && (
        <div className="d-flex align-items-center gap-2 px-2 py-1 bg-success bg-opacity-10 border-bottom border-success border-opacity-25 flex-shrink-0">
          <span className="small text-success fw-semibold">✍ <AutoTranslateText sourceLang="fr">Document signé — vous visualisez la version signée</AutoTranslateText></span>
          <button
            className="btn btn-sm btn-outline-secondary ms-auto py-0 px-2"
            style={{ fontSize: 11 }}
            onClick={() => { if (signedBlobUrl) URL.revokeObjectURL(signedBlobUrl); setSignedBlobUrl(null); setIsLoading(true); }}
          >
            <AutoTranslateText sourceLang="fr">Voir original</AutoTranslateText>
          </button>
        </div>
      )}

      {isSigningAreaVisible && (
        <div className="px-2 py-2 bg-light border-bottom">
          <p className="text-muted small mb-1"><AutoTranslateText sourceLang="fr">Dessinez votre signature ci-dessous.</AutoTranslateText></p>
          <div className="border bg-white mb-2">
            <SignaturePad
              ref={signaturePadRef}
              options={{ penColor: 'black', minWidth: 1, maxWidth: 2 }}
              canvasProps={{ style: { width: '100%', height: '100%', minHeight: '120px' } }}
            />
          </div>
          <div className="d-flex justify-content-end gap-1">
            <button type="button" className="btn btn-sm btn-secondary" onClick={() => signaturePadRef.current?.clear()} disabled={isSigning}><AutoTranslateText sourceLang="fr">Effacer</AutoTranslateText></button>
            <button type="button" className="btn btn-sm btn-primary" onClick={handleApplySignature} disabled={isSigning}><AutoTranslateText sourceLang="fr">Appliquer</AutoTranslateText></button>
          </div>
        </div>
      )}
      
      <div className="document-body position-relative flex-grow-1" style={{ overflow: 'auto', padding: '0', margin: '0', backgroundColor: '#f5f5f5' }}>
        {isLoading && <div className="position-absolute top-50 start-50 translate-middle"><div className="spinner-border"></div></div>}
        {hasError && <div className="position-absolute top-50 start-50 translate-middle text-danger"><i className="fas fa-exclamation-triangle"></i> Erreur</div>}
        <div style={{ visibility: isLoading || hasError ? 'hidden' : 'visible', height: '100%', width: '100%' }}>
          
          {/* ✅ CONTENEUR 100% LARGE : Le lecteur prend tout l'espace */}
          <div 
            ref={pdfContainerRef} 
            style={{ 
                width: '100%', // Le lecteur occupe toute la largeur disponible
                margin: '0', 
                overflowX: 'hidden', // Le scroll est géré à l'intérieur du PDFViewer
                height: '100%'
            }}
          >
            {/* Calcul de la largeur réelle du PDF (ex: 60% de la largeur du conteneur) */}
            {(() => {
                // S'assurer qu'on a un ratio valide (par défaut 0.6 pour 60%)
                const ratio = settings.containerWidth ? parseInt(settings.containerWidth, 10) / 100 : 0.6;
                // Largeur cible en pixels à transmettre au PDF
                const targetPdfWidth = pdfWidth ? pdfWidth * ratio : undefined;

                return (
                    <PDFViewer
                        url={signedBlobUrl || documentUrl}
                        width={targetPdfWidth}
                        height={displayMode === 'fixed' ? height : undefined}
                        onLoadError={handleLoadError}
                        onLoadSuccess={handleLoadSuccess}
                    />
                );
            })()}
          </div>
        </div>
      </div>
    </div>
  );
};

// ConfigComponent : voir ./document-viewer/DocumentViewerConfigComponent.tsx
const _NOOP = ({ config: _c, onConfigChange: _oc }: any) => {
  // dead — conservé pour compatibilité bundle uniquement, non référencé
  const [_tab] = useState('general');

  return null;
};

// L'objet plugin
const DocumentViewerPluginModular: PluginInterface = {
  metadata: {
    id: 'document-viewer',
    name: 'Visualiseur PDF', // Renommé pour clarté
    description: 'Afficher des documents PDF avec signature optionnelle',
    version: '2.0.0', // Version majeure incrémentée
    author: 'BookEditor Team',
    category: 'media',
    icon: '📄',
    tags: ['document', 'pdf', 'viewer']
  },
  
  ConfigComponent: DocumentViewerConfigComponent,
  PreviewComponent: DocumentViewerRenderer,
  
  generateHTML: (config: DocumentViewerConfig) => {
    // ✅ Toujours PDF
    const documentType = DOCUMENT_TYPES.pdf;
    
    // Génération de l'ID unique
    const instanceId = (config as any).id || `doc-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
    const configWithId = { ...config, id: instanceId };
    
    // Validation simple pour PDF
    const isValidFormat = config.documentUrl && config.documentUrl.toLowerCase().endsWith('.pdf');
    
    return generateUniformPlaceholder({
      pluginId: instanceId,
      pluginType: 'document-viewer',
      
      title: config.title || 'Visualiseur PDF',
      description: config.description || 'Document PDF',
      icon: '📄',
      category: 'media',
      stats: [
        `Type: ${documentType.icon} PDF`,
        `Source: ${config.isLocalFile ? '📁 Fichier local' : '🌐 URL externe'}`,
        `Dimensions: ${config.displayMode === 'fixed' ? `${config.width || 800} × ${config.height || 600} px` : 'Responsive'}`
      ],
      additionalInfo: config.documentUrl ? 
        (isValidFormat ? '✅ Prêt à afficher' : '⚠️ Format incorrect (PDF requis)') : 
        '❌ Aucun document sélectionné',
      
      config: configWithId
    });
  },

  // ⚠️ Checklist: mettre à jour documentationForAI à chaque évolution fonctionnelle.
  documentationForAI: {
    description: "Afficheur de documents PDF spécialisé. Idéal pour intégrer des menus, rapports, brochures ou manuels directement dans la page.",
    usage: "Utilise ce plugin pour tout besoin d'affichage de PDF. Si l'utilisateur demande d'afficher un document, un menu ou un rapport, c'est ce plugin qu'il faut utiliser.",
    
    // Définition des capacités pour l'IA
    capabilities: {
      viewing: "Affichage PDF natif via pdf.js (Pas de support Word/Excel/Office).",
      layout: "Largeur du contenu configurable (défaut 60%, centré), mode plein écran, responsive.",
      interaction: "Signature électronique (dessin), Téléchargement, Zoom.",
      styling: "Personnalisation complète du titre (Police, Taille, Gras, Italique, Couleur) et du fond (Couleur ou Transparent)."
    },

    // Schéma complet des propriétés configurables
    configSchema: {
      type: "object",
      properties: {
        // Source
        documentUrl: { type: "string", description: "URL directe du fichier PDF." },
        fileName: { type: "string", description: "Nom du fichier pour le téléchargement." },
        
        // Contenu
        title: { type: "string", description: "Titre affiché au-dessus du document." },
        
        // Apparence Conteneur
        // ✅ Correction : Suppression du point '.' invalide après la chaîne de description
        containerWidth: { type: "string", enum: ["100%", "90%", "80%", "60%", "50%", "40%"], description: "Largeur du visualiseur (défaut 60%)" },
        backgroundColor: { type: "string", description: "Couleur de fond de l'en-tête ou 'transparent'." },
        
        // Apparence Titre
        titleColor: { type: "string" },
        titleFontSize: { type: "string", enum: ["0.8rem", "1rem", "1.2rem", "1.5rem", "2rem"] },
        titleFontFamily: { type: "string", enum: ["Arial", "Helvetica", "Times New Roman", "Courier New"] },
        titleBold: { type: "boolean" },
        titleItalic: { type: "boolean" },
        titleUnderline: { type: "boolean" },

        // Fonctionnalités
        showDownloadButton: { type: "boolean", default: true },
        showFullscreenButton: { type: "boolean", default: true },
        enableSignature: { type: "boolean", description: "Active le panneau de signature.", default: false }
      }
    },

    // Exemple riche pour guider la génération
    exampleConfig: {
      title: "Menu de la semaine",
      documentUrl: "https://example.com/menu-restaurant.pdf",
      containerWidth: "60%",
      backgroundColor: "#ffffff",
      titleColor: "#d63384", // Exemple couleur marque
      titleFontSize: "1.5rem",
      titleBold: true,
      showDownloadButton: true,
      enableSignature: false
    }
  } as any,

  defaultConfig: {
    title: 'Mon Document PDF', 
    documentUrl: '', 
    // ✅ Correction : Force le type littéral pour satisfaire l'union stricte
    documentType: 'pdf' as 'pdf', 
    width: 800, 
    height: 600,
    displayMode: 'responsive',
    showDownloadButton: true, 
    showFullscreenButton: true, 
    description: '', 
    isLocalFile: false, 
    fileName: '', 
    enableSignature: false,
    signaturePositions: [],
    backgroundColor: '#ffffff',
    containerWidth: '60%',
    titleColor: '#212529',
    titleFontSize: '1rem',
    titleFontFamily: 'inherit'
  }
};

export default DocumentViewerPluginModular;
